import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr, fmtDateShort } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Pencil, Trash, FloppyDisk, X, UploadSimple, FilePdf, Certificate, MagnifyingGlass, Warning, CheckCircle, Clock } from "@phosphor-icons/react";

const STATUS_STYLES = {
  valid: { label: "Valid", cls: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle },
  expiring: { label: "Expiring soon", cls: "bg-amber-50 text-amber-700 border-amber-300", icon: Clock },
  expired: { label: "Expired", cls: "bg-rose-50 text-rose-700 border-rose-300", icon: Warning },
  no_expiry: { label: "No expiry", cls: "bg-slate-100 text-slate-600 border-slate-300", icon: Certificate },
};

function complianceFileLabel(file, hasExisting) {
  if (file) return file.name;
  return hasExisting ? "(existing file attached — choose to replace)" : "Click to attach PDF or image (optional)";
}

export function ComplianceStatusBadge({ status }) {
  const m = STATUS_STYLES[status] || STATUS_STYLES.valid;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-heading uppercase tracking-wider font-semibold border rounded-sm ${m.cls}`} data-testid={`compliance-status-${status}`}>
      <Icon size={11} weight="bold" /> {m.label}
    </span>
  );
}

const blank = {
  entity_type: "store", entity_id: "", doc_type: "gas_safety", title: "",
  reference_number: "", issued_by: "", issue_date: "", expiry_date: "", notes: "", file_id: null,
};

export function ComplianceModal({ open, onClose, presets = {}, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...blank, ...presets });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { data: docTypes = {} } = useQuery({ queryKey: ["compliance-doc-types"], queryFn: () => http.get("/compliance/doc-types").then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: equipment = [] } = useQuery({ queryKey: ["equipment"], queryFn: () => http.get("/equipment").then(r => r.data) });

  React.useEffect(() => {
    if (open) {
      setForm({ ...blank, ...presets });
      setFile(null);
      setErr("");
    }
  }, [open, presets]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      let file_id = form.file_id;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const r = await http.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = r.data.id;
      }
      const body = { ...form, file_id };
      if (form.id) {
        const { entity_type, entity_id, ...patch } = body;
        await http.patch(`/compliance/${form.id}`, patch);
      } else {
        await http.post("/compliance", body);
      }
      qc.invalidateQueries({ queryKey: ["compliance"] });
      onSaved && onSaved();
      onClose();
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="compliance-modal">
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div><div className="overline">{form.id ? "Edit" : "New"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Compliance document</h2></div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
        </div>

        {!form.id && !presets.entity_type && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline mb-1.5 block">Attach to *</label>
              <select value={form.entity_type} onChange={e => setForm({ ...form, entity_type: e.target.value, entity_id: "" })} data-testid="compliance-entity-type-select" className="rmms-input">
                <option value="store">Store</option>
                <option value="equipment">Equipment</option>
              </select>
            </div>
            <div>
              <label className="overline mb-1.5 block">{form.entity_type === "store" ? "Store" : "Equipment"} *</label>
              <select required value={form.entity_id} onChange={e => setForm({ ...form, entity_id: e.target.value })} data-testid="compliance-entity-select" className="rmms-input">
                <option value="">Select…</option>
                {form.entity_type === "store" ? stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>) : equipment.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="overline mb-1.5 block">Document type *</label>
            <select required value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })} data-testid="compliance-doctype-select" className="rmms-input">
              {Object.entries(docTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="overline mb-1.5 block">Reference number</label>
            <input value={form.reference_number} onChange={e => setForm({ ...form, reference_number: e.target.value })} data-testid="compliance-ref-input" className="rmms-input font-mono-data" />
          </div>
        </div>

        <div>
          <label className="overline mb-1.5 block">Title</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="compliance-title-input" className="rmms-input" placeholder="e.g. Annual Gas Safety Certificate" />
        </div>

        <div>
          <label className="overline mb-1.5 block">Issued by</label>
          <input value={form.issued_by} onChange={e => setForm({ ...form, issued_by: e.target.value })} data-testid="compliance-issuedby-input" className="rmms-input" placeholder="e.g. Gas Safe Engineering Ltd" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="overline mb-1.5 block">Issue date</label>
            <input type="date" value={form.issue_date || ""} onChange={e => setForm({ ...form, issue_date: e.target.value })} data-testid="compliance-issue-input" className="rmms-input font-mono-data" />
          </div>
          <div>
            <label className="overline mb-1.5 block">Expiry date</label>
            <input type="date" value={form.expiry_date || ""} onChange={e => setForm({ ...form, expiry_date: e.target.value })} data-testid="compliance-expiry-input" className="rmms-input font-mono-data" />
          </div>
        </div>

        <div>
          <label className="overline mb-1.5 block">Document (PDF / image)</label>
          <label className="flex items-center gap-3 p-3 border border-dashed border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50">
            <UploadSimple size={18} className="text-slate-500" />
            <span className="text-sm text-slate-600 truncate" data-testid="compliance-file-name">{complianceFileLabel(file, !!form.file_id)}</span>
            <input data-testid="compliance-file-input" type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" />
          </label>
        </div>

        <div>
          <label className="overline mb-1.5 block">Notes</label>
          <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="compliance-notes-input" className="w-full rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-[#002FA7]" />
        </div>

        {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <button type="button" onClick={onClose} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
          <button type="submit" disabled={busy} data-testid="compliance-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
            <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Compliance() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [entityType, setEntityType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [q, setQ] = useState("");

  const { data: docs = [] } = useQuery({ queryKey: ["compliance"], queryFn: () => http.get("/compliance").then(r => r.data) });
  const { data: docTypes = {} } = useQuery({ queryKey: ["compliance-doc-types"], queryFn: () => http.get("/compliance/doc-types").then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: equipment = [] } = useQuery({ queryKey: ["equipment"], queryFn: () => http.get("/equipment").then(r => r.data) });
  const storeMap = Object.fromEntries(stores.map(s => [s.id, s.name]));
  const eqMap = Object.fromEntries(equipment.map(e => [e.id, e]));

  const canCreate = ["admin", "general_manager", "maintenance_manager", "district_manager", "area_manager", "store_manager"].includes(user?.role);
  const canDelete = ["admin", "general_manager", "maintenance_manager"].includes(user?.role);

  const filtered = useMemo(() => docs.filter(d => {
    if (entityType && d.entity_type !== entityType) return false;
    if (statusFilter && d.status !== statusFilter) return false;
    if (storeFilter) {
      // doc.store_id is set directly for store-level docs; for equipment-level docs, resolve via equipment
      const docStore = d.entity_type === "equipment" ? eqMap[d.entity_id]?.store_id : d.store_id;
      if (docStore !== storeFilter) return false;
    }
    if (q) {
      const hay = `${docTypes[d.doc_type] || d.doc_type} ${d.title || ""} ${d.issued_by || ""} ${d.reference_number || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [docs, entityType, statusFilter, storeFilter, q, docTypes, eqMap]);

  const counts = useMemo(() => ({
    total: docs.length,
    valid: docs.filter(d => d.status === "valid").length,
    expiring: docs.filter(d => d.status === "expiring").length,
    expired: docs.filter(d => d.status === "expired").length,
  }), [docs]);

  const [modal, setModal] = useState(null);

  const remove = async (id, label) => {
    if (!window.confirm(`Soft-delete "${label}"?`)) return;
    try { await http.delete(`/compliance/${id}`); qc.invalidateQueries({ queryKey: ["compliance"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };
  const viewFile = async (fid) => {
    try {
      const r = await http.get(`/files/${fid}`, { responseType: "blob", validateStatus: () => true });
      if (r.status < 200 || r.status >= 300) { alert("File missing or expired."); return; }
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { console.error("[compliance] viewFile failed", e); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Safety & Compliance</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1 flex items-center gap-3"><Certificate size={26} /> Compliance documents</h1>
          <p className="text-sm text-slate-600 mt-1">Gas Safety · Pest Control · Fire · Electrical · Food Hygiene · PAT · Calibration</p>
        </div>
        {canCreate && (
          <button onClick={() => setModal({})} data-testid="compliance-new-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
            <Plus size={14} weight="bold" /> Upload document
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <button onClick={() => setStatusFilter("")} className={`rmms-card p-5 text-left transition-colors ${statusFilter === "" ? "border-[#002FA7]" : ""}`} data-testid="compliance-kpi-total">
          <div className="overline">Total</div><div className="text-3xl font-mono-data mt-2 tabular-nums">{counts.total}</div>
        </button>
        <button onClick={() => setStatusFilter("valid")} className={`rmms-card p-5 text-left transition-colors ${statusFilter === "valid" ? "border-emerald-400" : ""}`} data-testid="compliance-kpi-valid">
          <div className="overline">Valid</div><div className="text-3xl font-mono-data mt-2 tabular-nums text-emerald-600">{counts.valid}</div>
        </button>
        <button onClick={() => setStatusFilter("expiring")} className={`rmms-card p-5 text-left transition-colors ${statusFilter === "expiring" ? "border-amber-400" : ""}`} data-testid="compliance-kpi-expiring">
          <div className="overline">Expiring &lt;30d</div><div className="text-3xl font-mono-data mt-2 tabular-nums text-amber-600">{counts.expiring}</div>
        </button>
        <button onClick={() => setStatusFilter("expired")} className={`rmms-card p-5 text-left transition-colors ${statusFilter === "expired" ? "border-rose-400" : ""}`} data-testid="compliance-kpi-expired">
          <div className="overline">Expired</div><div className="text-3xl font-mono-data mt-2 tabular-nums text-rose-600">{counts.expired}</div>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="compliance-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search type, issuer, ref…"
            className="h-9 pl-8 pr-3 text-sm border border-slate-200 rounded-md w-72 outline-none focus:border-[#002FA7]" />
        </div>
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden">
          {[["", "All"], ["store", "Store"], ["equipment", "Equipment"]].map(([v, l]) => (
            <button key={v || "all"} onClick={() => setEntityType(v)} data-testid={`compliance-filter-${v || "all"}`}
              className={`px-3 h-9 text-xs font-heading uppercase tracking-wider ${entityType === v ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <select
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
          data-testid="compliance-store-filter"
          className="h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7] bg-white"
        >
          <option value="">All stores</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {storeFilter && (
          <button data-testid="compliance-store-filter-clear" onClick={() => setStoreFilter("")} className="text-[11px] text-slate-500 hover:text-rose-600 underline">Clear store</button>
        )}
      </div>

      <div className="rmms-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="px-4 py-2.5">Document</th><th className="px-4 py-2.5">Attached to</th><th className="px-4 py-2.5">Issuer</th><th className="px-4 py-2.5">Expiry</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 w-32"></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500" data-testid="compliance-empty">No compliance documents match.</td></tr>}
            {filtered.map(d => (
              <tr key={d.id} data-testid={`compliance-row-${d.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{docTypes[d.doc_type] || d.doc_type}</div>
                  {d.title && <div className="text-xs text-slate-500">{d.title}</div>}
                  {d.reference_number && <div className="text-[11px] text-slate-400 font-mono-data mt-0.5">REF: {d.reference_number}</div>}
                </td>
                <td className="px-4 py-3 text-[12px] text-slate-600">
                  {d.entity_type === "store"
                    ? <>{storeMap[d.entity_id] || "—"}<div className="text-[10px] uppercase tracking-wider text-slate-400">Store</div></>
                    : <>{eqMap[d.entity_id]?.name || "—"}<div className="text-[10px] uppercase tracking-wider text-slate-400">Equipment · {storeMap[eqMap[d.entity_id]?.store_id] || ""}</div></>}
                </td>
                <td className="px-4 py-3 text-[12px] text-slate-600">{d.issued_by || "—"}</td>
                <td className="px-4 py-3 font-mono-data text-[12px]">{d.expiry_date ? fmtDateShort(d.expiry_date) : "—"}</td>
                <td className="px-4 py-3"><ComplianceStatusBadge status={d.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {d.file_id && <button onClick={() => viewFile(d.file_id)} data-testid={`compliance-view-${d.id}`} className="h-7 px-2 text-[11px] text-[#002FA7] inline-flex items-center gap-1 border border-slate-200 rounded-sm hover:bg-slate-50"><FilePdf size={11} weight="bold"/> View</button>}
                    {canCreate && <button onClick={() => setModal({ ...d })} data-testid={`compliance-edit-${d.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                    {canDelete && <button onClick={() => remove(d.id, d.title || d.doc_type)} data-testid={`compliance-delete-${d.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ComplianceModal open={!!modal} onClose={() => setModal(null)} presets={modal && modal.id ? modal : {}} />
    </div>
  );
}
